/**
 * CallTimer - Displays call duration with proper formatting
 * Shows connecting state, call duration, and hold time
 */

'use client';

import React, { useEffect, useState } from 'react';
import { Pause } from 'lucide-react';
import type { CallTimerProps, CallState } from '../types';

/**
 * Format seconds into MM:SS or HH:MM:SS format
 */
export function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return '00:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get status text based on call state
 */
function getStatusText(callState: CallState): string {
  switch (callState) {
    case 'initiating':
      return 'Calling...';
    case 'ringing':
      return 'Ringing...';
    case 'connecting':
      return 'Connecting...';
    case 'reconnecting':
      return 'Reconnecting...';
    case 'ended':
      return 'Call Ended';
    default:
      return '';
  }
}

export function CallTimer({
  callState,
  callDuration,
  isOnHold = false,
  holdStartTime = null,
  className = '',
  statusTextOverride = null,
}: CallTimerProps) {
  const [holdDuration, setHoldDuration] = useState(0);
  // Self-managed timer for Capacitor resilience
  // (parent setInterval can be throttled/killed on mobile WebViews)
  const [selfDuration, setSelfDuration] = useState(0);
  const connectedAtRef = React.useRef<number | null>(null);
  
  // Start self-managed timer when connected
  useEffect(() => {
    if (callState === 'connected') {
      if (!connectedAtRef.current) {
        connectedAtRef.current = Date.now();
      }
      const start = connectedAtRef.current;
      const interval = setInterval(() => {
        setSelfDuration(Math.floor((Date.now() - start) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    } else {
      connectedAtRef.current = null;
    }
  }, [callState]);
  
  // Use whichever duration is larger (parent or self-managed)
  const displayDuration = Math.max(callDuration, callState === 'connected' ? selfDuration : 0);
  
  // Update hold duration every second when on hold
  useEffect(() => {
    if (!isOnHold || !holdStartTime) {
      return;
    }
    
    const interval = setInterval(() => {
      setHoldDuration(Math.floor((Date.now() - holdStartTime.getTime()) / 1000));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOnHold, holdStartTime]);
  
  // Show hold indicator
  if (isOnHold && callState === 'connected') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Pause className="w-4 h-4 text-yellow-400" />
        <span className="text-yellow-400 font-medium">
          On Hold {holdDuration > 0 && `(${formatDuration(holdDuration)})`}
        </span>
      </div>
    );
  }
  
  // Show call duration when connected
  if (callState === 'connected') {
    return (
      <span className={`font-mono text-white/90 ${className}`}>
        {formatDuration(displayDuration)}
      </span>
    );
  }
  
  // Show status text for other states
  const statusText = statusTextOverride ?? getStatusText(callState);
  if (statusText) {
    return (
      <span className={`text-gray-400 ${className}`}>
        {statusText}
      </span>
    );
  }
  
  return null;
}

export default CallTimer;
